import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './PdfWordStyles.scss';
import { InfoService } from '../../../services/site/InfoService';
import VideoPlayer from '../../../Components/VideoPlayer';
import templateVideo from "../../../assets/videos/template.mp4";
// Import SCSS: import './sds_template_styles.scss';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12 } }
};

const scaleIn = {
  hidden: { scale: 0.8, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } }
};

// Animated section wrapper
const AnimatedSection: React.FC<{ 
  children: React.ReactNode; 
  className?: string;
  variant?: any;
}> = ({ children, className = '', variant = fadeInUp }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variant}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Hero Section
const HeroSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_template-hero section">
      <div className="container">
        <div className="columns is-vcentered">
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="title is-1 sds_template-hero-title">
                PDF/Word Template Automation
              </h1>
              <h2 className="subtitle is-4 sds_template-hero-subtitle mb-5">
                Auto-fill, Merge, Compress Thousands of Documents in Seconds
              </h2>
              <div className="content is-size-5 sds_template-hero-description">
                <p className="mb-4">
                  <strong>What is Template Automation?</strong> It's intelligent document generation that automatically 
                  fills your data into professional templates, merges multiple files, and compresses them - all without 
                  manual work.
                </p>
                <p className="mb-4">
                  Stop creating invoices, certificates, reports, or letters one by one. Our system connects with your 
                  CRM, Excel, or database to generate thousands of perfectly formatted documents instantly.
                </p>
              </div>
              <div className="sds_template-feature-badges">
                <span className="tag is-large sds_template-badge">📝 Auto-Fill</span>
                <span className="tag is-large sds_template-badge">🔗 Merge</span>
                <span className="tag is-large sds_template-badge">📦 Compress</span>
                <span className="tag is-large sds_template-badge">⚡ Bulk Process</span>
              </div>
            </motion.div>
          </div>
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="sds_template-hero-visual"
            >
              <div className="sds_template-animation">
                <motion.div 
                  className="sds_template-doc-icon"
                  animate={{ 
                    y: [0, -20, 0],
                    rotate: [0, 3, -3, 0]
                  }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  📄
                </motion.div>
                <div className="sds_template-floating-icons">
                  {['📝', '🔗', '📦', '✅'].map((emoji, i) => (
                    <motion.div
                      key={i}
                      className="sds_template-floating-icon"
                      animate={{ 
                        y: [-15, 15, -15],
                        opacity: [0.7, 1, 0.7]
                      }}
                      transition={{ 
                        duration: 3 + i * 0.5, 
                        repeat: Infinity,
                        delay: i * 0.2 
                      }}
                    >
                      {emoji}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Video Demonstration */}
        <div className="sds_template-video-container mt-6">
          <VideoPlayer
            videoSrc={templateVideo}
            title="Template Automation in Action"
            description="Watch how to use templates for single/bulk signing, auto-fill forms, merge multiple documents, compress files, and integrate with powerful APIs - all demonstrated in real-time"
            lightBackground={false}
          />
        </div>
      </div>
    </AnimatedSection>
  );
};

// Core Features Section
const CoreFeaturesSection: React.FC = () => {
  const features = [
    { icon: '📝', title: 'Auto-Fill Templates', desc: 'Automatically populate templates with data from Excel, CRM, or database. Names, addresses, amounts - everything fills instantly!' },
    { icon: '🔗', title: 'Merge Documents', desc: 'Combine multiple PDFs or Word documents into one file. Perfect for reports, proposals, and documentation packages!' },
    { icon: '📦', title: 'Compress Files', desc: 'Reduce file sizes by up to 90% without losing quality. Email large documents easily and save storage space!' }
  ];

  return (
    <AnimatedSection className="sds_template-features section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_template-section-title">🎯 Three Powerful Features in One Solution</h2>
        </div>

        <motion.div 
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} className="column" variants={scaleIn}>
              <motion.div 
                className="sds_template-feature-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <div className="sds_template-feature-icon">{feature.icon}</div>
                <h3 className="title is-4 has-text-centered mb-3">{feature.title}</h3>
                <p className="has-text-centered">{feature.desc}</p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Process Flow Section
const ProcessFlowSection: React.FC = () => {
  const steps = [
    { icon: '📤', title: 'Upload Template', desc: 'Upload PDF/Word template with placeholders' },
    { icon: '🔗', title: 'Connect Data', desc: 'Link to your CRM, Excel, or database' },
    { icon: '⚙️', title: 'Configure Options', desc: 'Choose merge, compress, or both' },
    { icon: '✅', title: 'Generate & Download', desc: 'Get perfectly formatted documents instantly' }
  ];

  return (
    <AnimatedSection className="sds_template-process section sds_template-bg-gradient">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_template-section-title has-text-white">
            How It Works - Simple 4-Step Process
          </h2>
        </div>

        <motion.div 
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <motion.div className="column" variants={fadeInUp}>
                <div className="sds_template-process-card">
                  <div className="sds_template-step-number">{index + 1}</div>
                  <div className="sds_template-step-icon">{step.icon}</div>
                  <h4 className="title is-5 has-text-centered mb-3">{step.title}</h4>
                  <p className="has-text-centered has-text-grey-dark">{step.desc}</p>
                </div>
              </motion.div>
              {index < steps.length - 1 && (
                <div className="column is-narrow is-hidden-mobile sds_template-arrow-container">
                  <motion.span 
                    className="sds_template-arrow"
                    animate={{ x: [0, 10, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.2 }}
                  >
                    →
                  </motion.span>
                </div>
              )}
            </React.Fragment>
          ))}
        </motion.div>

  <motion.div
  className="sds_template-highlight-badge has-text-centered is-flex is-justify-content-center"
  animate={{ scale: [1, 1.05, 1] }}
  transition={{ duration: 2, repeat: Infinity }}
>
  ⚡ Process 10,000+ Documents in Under 5 Minutes! ⚡
</motion.div>

      </div>
    </AnimatedSection>
  );
};

// Use Cases Section
const UseCasesSection: React.FC = () => {
  const useCases = [
    { icon: '👥', title: 'HR Department', color: '#4CAF50', desc: 'Generate offer letters, appointment orders, ID cards, and payslips automatically.', benefits: ['Offer letters with employee details', 'Appointment orders & confirmations', 'Salary slips & increment letters', 'Experience & relieving certificates', 'Employee ID cards & badges'] },
    { icon: '💰', title: 'Accounts & Finance', color: '#FF9800', desc: 'Create invoices, receipts, purchase orders, and financial statements automatically.', benefits: ['GST invoices with calculations', 'Payment receipts & vouchers', 'Purchase orders & quotations', 'Financial reports & statements', 'Tax documents & certificates'] },
    { icon: '📈', title: 'Sales & Marketing', color: '#9C27B0', desc: 'Generate personalized proposals, quotations, and contracts for each client.', benefits: ['Customized sales proposals', 'Price quotations & estimates', 'Client contracts & agreements', 'Marketing materials & brochures', 'Personalized email attachments'] },
    { icon: '🎓', title: 'Education & Training', color: '#F44336', desc: 'Create certificates, mark sheets, ID cards, and admission letters in bulk.', benefits: ['Student certificates & diplomas', 'Mark sheets & report cards', 'Student ID cards', 'Admission & fee receipts', 'Parent communication letters'] },
    { icon: '🏥', title: 'Healthcare & Medical', color: '#00BCD4', desc: 'Generate prescriptions, lab reports, patient records, and medical certificates.', benefits: ['Prescription templates', 'Lab test reports', 'Medical certificates', 'Patient discharge summaries', 'Insurance claim documents'] },
    { icon: '⚖️', title: 'Legal Services', color: '#8BC34A', desc: 'Create legal notices, contracts, agreements, and court documents.', benefits: ['Legal notices & summons', 'Contract drafts', 'Agreement documents', 'Court filings & petitions', 'Client engagement letters'] },
    { icon: '🏠', title: 'Real Estate', color: '#673AB7', desc: 'Generate property brochures, sale agreements, rental contracts, and booking forms.', benefits: ['Property brochures & listings', 'Sale agreements & deeds', 'Rental/lease agreements', 'Booking & allotment letters', 'Property tax documents'] },
    { icon: '🛡️', title: 'Insurance Companies', color: '#FF5722', desc: 'Create policy documents, premium receipts, claim forms, and renewal notices.', benefits: ['Policy documents & certificates', 'Premium payment receipts', 'Claim settlement letters', 'Renewal notices', 'Proposal forms'] }
  ];

  return (
    <AnimatedSection className="sds_template-usecases section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_template-section-title">Who Needs Template Automation?</h2>
          <p className="subtitle is-5">Save hours every day across all departments</p>
        </div>

        <motion.div 
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {useCases.map((useCase, index) => (
            <motion.div key={index} className="column is-4" variants={scaleIn}>
              <motion.div 
                className="sds_template-usecase-card"
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ borderLeftColor: useCase.color }}
              >
                <div className="sds_template-usecase-icon" style={{ background: `linear-gradient(135deg, ${useCase.color}, ${useCase.color}dd)` }}>
                  {useCase.icon}
                </div>
                <h3 className="title is-5 has-text-centered mb-3">{useCase.title}</h3>
                <p className="has-text-centered has-text-grey mb-4">{useCase.desc}</p>
                <ul className="sds_template-benefits-list">
                  {useCase.benefits.map((benefit, i) => (
                    <li key={i}>
                      <span className="sds_template-check-icon" style={{ background: useCase.color }}>✓</span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Bulk Processing Section
const BulkProcessingSection: React.FC = () => {
  const stats = [
    { number: '50,000+', label: 'Documents per Hour' },
    { number: '100+', label: 'Concurrent Templates' },
    { number: '99.9%', label: 'Accuracy Rate' },
    { number: '24/7', label: 'Automated Processing' }
  ];

  return (
    <AnimatedSection className="sds_template-bulk section sds_template-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_template-section-title">⚡ Bulk Processing Power</h2>
          <p className="subtitle is-5">Handle massive volumes with ease</p>
        </div>

        <motion.div 
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {stats.map((stat, index) => (
            <motion.div key={index} className="column is-3" variants={scaleIn}>
              <motion.div 
                className="sds_template-stat-card"
                whileHover={{ scale: 1.05 }}
              >
                <p className="sds_template-stat-number">{stat.number}</p>
                <p className="sds_template-stat-label">{stat.label}</p>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// Configuration Options Section
const ConfigurationSection: React.FC = () => {
  const configs = [
    { icon: '📐', name: 'Page Size & Orientation' },
    { icon: '🎨', name: 'Fonts & Colors' },
    { icon: '🖼️', name: 'Logo & Watermarks' },
    { icon: '📏', name: 'Margins & Spacing' },
    { icon: '🔢', name: 'Page Numbering' },
    { icon: '📎', name: 'Header & Footer' },
    { icon: '🔒', name: 'Password Protection' },
    { icon: '📊', name: 'Tables & Charts' },
    { icon: '🖨️', name: 'Print Settings' },
    { icon: '📧', name: 'Email Delivery' },
    { icon: '☁️', name: 'Cloud Storage' },
    { icon: '🗂️', name: 'File Naming Rules' }
  ];

  return (
    <AnimatedSection className="sds_template-config section">
      <div className="container">
        <div className="box sds_template-config-box">
          <div className="has-text-centered mb-5">
            <h2 className="title is-2 sds_template-section-title">🎛️ Multiple Configuration Options</h2>
            <p className="subtitle is-5">Customize everything to match your needs</p>
          </div>

          <motion.div 
            className="columns is-multiline"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {configs.map((config, index) => (
              <motion.div key={index} className="column is-3" variants={scaleIn}>
                <motion.div 
                  className="sds_template-config-item"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="sds_template-config-icon">{config.icon}</div>
                  <p className="has-text-weight-bold">{config.name}</p>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>

          <div className="notification is-info is-light mt-5">
            <p className="has-text-centered has-text-weight-bold is-size-5 mb-2">
              💡 Save Configuration Profiles
            </p>
            <p className="has-text-centered">
              Create unlimited configuration profiles for different document types. Switch between them instantly!
            </p>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// CRM Integration Section
const CRMIntegrationSection: React.FC = () => {
  const crms = [
    { icon: '💼', title: 'Salesforce', color: '#4CAF50', desc: 'Auto-generate quotes, proposals, and contracts directly from Salesforce opportunities.', benefits: ['One-click document generation', 'Real-time data sync', 'Attach to records automatically', 'Email directly from Salesforce'] },
    { icon: '🎯', title: 'Zoho CRM', color: '#FF9800', desc: 'Generate invoices, receipts, and customer documents directly from Zoho CRM records.', benefits: ['Native Zoho integration', 'Custom button placement', 'Workflow automation', 'Blueprint integration'] },
    { icon: '📊', title: 'Microsoft Dynamics', color: '#2196F3', desc: 'Create business documents from Dynamics 365 customer and sales data automatically.', benefits: ['Dynamics 365 integration', 'Power Automate workflows', 'SharePoint storage', 'Teams notifications'] }
  ];

  return (
    <AnimatedSection className="sds_template-crm section sds_template-bg-gradient">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_template-section-title has-text-white">
            🔄 Seamless CRM Integration
          </h2>
          <p className="subtitle is-5 has-text-white-ter">Connect in minutes!</p>
        </div>

        <motion.div 
          className="columns"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {crms.map((crm, index) => (
            <motion.div key={index} className="column" variants={fadeInUp}>
              <motion.div 
                className="sds_template-crm-card"
                whileHover={{ y: -8 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{ borderLeftColor: crm.color }}
              >
                <div className="sds_template-crm-icon" style={{ background: `linear-gradient(135deg, ${crm.color}, ${crm.color}dd)` }}>
                  {crm.icon}
                </div>
                <h3 className="title is-4 has-text-centered mb-3">{crm.title}</h3>
                <p className="has-text-centered has-text-grey-dark mb-4">{crm.desc}</p>
                <ul className="sds_template-benefits-list">
                  {crm.benefits.map((benefit, i) => (
                    <li key={i}>
                      <span className="icon has-text-success is-small">
                        <i className="fas fa-check-circle"></i>
                      </span>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

// ROI Comparison Section
const ROIComparisonSection: React.FC = () => {
  const comparisons = [
    { task: '100 Invoices', manual: '5 Hours', automated: '2 Minutes' },
    { task: '1000 Certificates', manual: '2-3 Days', automated: '15 Minutes' },
    { task: 'Error Rate', manual: '5-10%', automated: '0.1%' },
    { task: 'Monthly Cost', manual: '₹50,000+ (Staff Time)', automated: '₹5,000 (Software)' },
    { task: 'Scalability', manual: 'Limited by Staff', automated: 'Unlimited ♾️' }
  ];

  return (
    <AnimatedSection className="sds_template-roi section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_template-section-title">💰 Save Money, Save Time</h2>
          <p className="subtitle is-5">ROI Calculator</p>
        </div>

        <div className="box">
          <div className="table-container">
            <table className="table is-fullwidth is-hoverable sds_template-roi-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th className="has-text-centered">❌ Manual Process</th>
                  <th className="has-text-centered">✅ HDT Automation</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, index) => (
                  <motion.tr 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.08 }}
                    viewport={{ once: true }}
                  >
                    <td className="has-text-weight-bold">{row.task}</td>
                    <td className="has-text-centered has-text-grey">{row.manual}</td>
                    <td className="has-text-centered has-text-success has-text-weight-bold">{row.automated}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="notification is-success is-light mt-5">
            <p className="has-text-centered is-size-5 has-text-weight-bold">
              💡 Average ROI: <span className="has-text-success">300-500%</span> in the first year!
            </p>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// CTA Section
const CTASection: React.FC = () => {
  return (
    <AnimatedSection className="sds_template-cta section">
      <div className="container">
        <div className="box sds_template-cta-box">
          <div className="has-text-centered">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="title is-1 mb-5">Ready to Automate Your Document Creation?</h2>
              <p className="subtitle is-4 mb-6">
                Stop wasting hours on repetitive document tasks
              </p>
              
              <div className="notification is-success is-light mb-5">
                {/* <p className="is-size-4 has-text-weight-bold mb-3">🎁 Special Launch Offer</p> */}
                <div className="content">
                  <p className="mb-2">✅ First 5 Documents FREE</p>
                  <p className="mb-2">✅ Free CRM Integration Setup</p>
                  <p className="mb-2">✅ 24/7 Priority Support for 3 Months</p>
                  <p className="is-size-6 has-text-danger has-text-weight-semibold">No credit card required • Setup in 10 minutes • Cancel anytime</p>
                </div>
              </div>

              <div className="buttons is-centered are-large">
                <motion.a 
                  href="tel:+91 9553896536" 
                  className="button is-primary is-large sds_template-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-phone"></i>
                  </span>
                  <span>Call Now: {InfoService.mobile_no_1}</span>
                </motion.a>
                <motion.a 
                    href={InfoService.loginLink}
                  className="button is-info is-large sds_template-cta-button"
                  whileHover={{ scale: 1.05, y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">
                    <i className="fas fa-envelope"></i>
                  </span>
                  <span>Start Free Trial</span>
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

// Main Component
const PdfWordTemplate: React.FC = () => {
  return (
    <div className="sds_template-wrapper">
      {/* SEO Meta Tags */}
      <div style={{ display: 'none' }}>
        <h1>HDT Solutions - PDF/Word Template Automation & Document Generation</h1>
        <meta name="description" content="Automate document creation with template auto-fill, merge, and compress. Generate thousands of PDFs and Word documents instantly from your CRM, Excel, or database." />
        <meta name="keywords" content="template automation, document generation, PDF auto-fill, Word templates, bulk document processing, merge PDFs, compress files, HDT Solutions" />
      </div>

      <HeroSection />
      <CoreFeaturesSection />
      <ProcessFlowSection />
      <UseCasesSection />
      <BulkProcessingSection />
      <ConfigurationSection />
      <CRMIntegrationSection />
      <ROIComparisonSection />
      <CTASection />
    </div>
  );
};

export default PdfWordTemplate;